import { useEffect, useState } from "react";
import { Sprout, HeartHandshake, Dna, MapPin } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHero, SectionTitle } from "@/components/site/PageHero";

const PILLARS = [
  { icon: Dna, title: "Selección genética", text: "Elegimos cada animal por fertilidad, precocidad, aplomos y conformación carnicera, apoyados en registros y evaluación genética." },
  { icon: Sprout, title: "Criados a campo", text: "Nuestros animales crecen sobre pasturas tropicales: rústicos, adaptados al calor y listos para trabajar en cualquier rodeo." },
  { icon: HeartHandshake, title: "Trato familiar", text: "Acompañamos a cada productor antes y después de la compra. La confianza se construye con palabra y con resultados." },
];

const TEAM_PHOTO = "https://customer-assets-agu9un31.emergentagent.net/job_borrador-creativo/artifacts/3df14753df1d8653_b9d463b0-42fe-4cce-bead-c44678748ab6.jpg";

export default function Cabana() {
  const { settings } = useApp();
  const [imgs, setImgs] = useState([]);
  useEffect(() => { api.get("/media").then((r) => setImgs(r.data.filter((m) => m.type === "image"))); }, []);
  const photo = imgs.find((m) => m.url.includes("856ec4c98d611446")) || imgs[0];

  return (
    <div data-testid="cabana-page">
      <PageHero testid="cabana-hero" eyebrow="Nosotros" title="Una familia, un campo y una raza" image={fileUrl(photo?.url)}
        text="La historia de Hacienda Fernández & Vargas se escribe todos los días, a caballo y entre los rodeos." />

      <section className="container-fv py-20 sm:py-28 grid gap-14 lg:grid-cols-12 items-center">
        <div className="lg:col-span-6">
          <SectionTitle eyebrow="Nuestra historia" title="Tradición ganadera, mirada puesta en el futuro" />
          <div className="mt-8 space-y-5 text-[#5C635E] leading-relaxed" data-testid="cabana-history">
            <p>Hacienda Fernández &amp; Vargas nació del encuentro de ideas de sus fundadores, Vinicio Vargas y Marcelino Fernández, para desarrollar un tope de línea en genética cárnica adaptable al trópico, que responda a las nuevas necesidades del mercado, a las cuales no puede responder por sí solo ni competir el binomio Nelore-Brahman.</p>
            <p>Con los años apostamos por la raza que mejor combina la rusticidad del cebú con la calidad carnicera del Angus. Cada generación de animales es el resultado de decisiones pacientes, tomadas en el corral y en la manga.</p>
            <p>Hoy ofrecemos genética elite de la raza, basada en nuestras mejores donadoras y en las de nuestros socios en Argentina, Cabaña Tres Cruces, que incluye además la combinación con toros como Luquense 467 “Mundial”, el Gran Campeón Macho del Congreso Mundial Brangus 2023 (Argentina), el toro promesa Agucho, Ingá, Don Ciriaco, Profesor, Pirayú, Viamonte y Sorpresa, entre otros, para llevar lo mejor de esta genética al trópico.</p>
          </div>
        </div>
        <div className="lg:col-span-6">
          <img src={TEAM_PHOTO} alt="Equipo de Hacienda Fernández & Vargas" data-testid="cabana-photo" className="aspect-[3/4] w-full max-w-md mx-auto object-cover" />
        </div>
      </section>

      <section className="bg-[#F3EAD7]/60 py-20 sm:py-28">
        <div className="container-fv">
          <SectionTitle eyebrow="Filosofía de trabajo" title="Lo que buscamos en cada animal" className="max-w-2xl" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="card-fv p-8" data-testid={`cabana-pillar-${title.split(" ")[0].toLowerCase()}`}>
                <Icon className="text-[#8B2B22]" size={28} />
                <h3 className="f-display mt-5 text-2xl">{title}</h3>
                <p className="mt-3 text-sm text-[#5C635E] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-fv py-20 sm:py-28 grid gap-12 lg:grid-cols-12 items-center">
        <div className="lg:col-span-5">
          <SectionTitle eyebrow="Ubicación" title="Desde Costa Rica, genética para toda la región" />
          <p className="mt-6 text-[#5C635E] leading-relaxed">Trabajamos desde San José, Costa Rica, con animales criados a pasto y adaptados al clima tropical, y llevamos nuestra genética a productores de toda Latinoamérica.</p>
          <p className="mt-6 flex gap-3 font-semibold" data-testid="cabana-address"><MapPin className="text-[#8B2B22]" size={20} />{settings.address}</p>
        </div>
        <div className="lg:col-span-7 overflow-hidden border border-[#8B2B22]/10">
          <iframe title="Mapa" data-testid="cabana-map" className="h-[380px] w-full grayscale-[40%]" loading="lazy"
            src={`https://www.google.com/maps?q=${encodeURIComponent(settings.address || "San José, Costa Rica")}&output=embed`} />
        </div>
      </section>
    </div>
  );
}
